import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Pencil, Trash2, Upload } from "lucide-react";

const BASE_URL = "/v1";
const COMPRESSIBLE_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

const formatFileSize = (bytes) =>
    bytes < 1024 * 1024
        ? `${(bytes / 1024).toFixed(1)} KB`
        : `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

const compressImageFile = async (file) => {
    if (file.type === "application/pdf") {
        const [pdfjsLib, jsPDFModule] = await Promise.all([
            import("pdfjs-dist"),
            import("jspdf"),
        ]);
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
            "pdfjs-dist/build/pdf.worker.min.mjs",
            import.meta.url
        ).toString();
        const pdfData = new Uint8Array(await file.arrayBuffer());
        const pdfDocument = await pdfjsLib.getDocument({ data: pdfData }).promise;
        let compressedPdf;

        try {
            for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
                const page = await pdfDocument.getPage(pageNumber);
                const viewport = page.getViewport({ scale: 1 });
                const scale = Math.min(1.7, 2600 / Math.max(viewport.width, viewport.height));
                const renderViewport = page.getViewport({ scale });
                const canvas = document.createElement("canvas");
                canvas.width = Math.ceil(renderViewport.width);
                canvas.height = Math.ceil(renderViewport.height);
                const context = canvas.getContext("2d", { alpha: false });
                if (!context) throw new Error("Unable to prepare PDF page for compression");

                context.fillStyle = "#fff";
                context.fillRect(0, 0, canvas.width, canvas.height);
                await page.render({ canvasContext: context, viewport: renderViewport }).promise;
                const pageImage = canvas.toDataURL("image/jpeg", 0.72);
                if (!compressedPdf) {
                    compressedPdf = new jsPDFModule.jsPDF({
                        unit: "pt",
                        format: [viewport.width, viewport.height],
                        orientation: viewport.width > viewport.height ? "landscape" : "portrait",
                        compress: true,
                    });
                } else {
                    compressedPdf.addPage(
                        [viewport.width, viewport.height],
                        viewport.width > viewport.height ? "landscape" : "portrait"
                    );
                }
                compressedPdf.addImage(pageImage, "JPEG", 0, 0, viewport.width, viewport.height);
                canvas.width = 0;
                canvas.height = 0;
                page.cleanup();
            }
        } finally {
            await pdfDocument.destroy();
        }

        const compressedBlob = compressedPdf?.output("blob");
        if (!compressedBlob || compressedBlob.size >= file.size) return file;
        return new File([compressedBlob], file.name, { type: "application/pdf", lastModified: file.lastModified });
    }

    if (!COMPRESSIBLE_IMAGE_TYPES.has(file.type)) return file;

    const image = await createImageBitmap(file);
    try {
        const scale = Math.min(1, 2560 / Math.max(image.width, image.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const context = canvas.getContext("2d");
        if (!context) throw new Error("Unable to prepare image for compression");
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        const blob = await new Promise((resolve) => {
            canvas.toBlob(resolve, file.type, file.type === "image/png" ? undefined : 0.82);
        });
        if (!blob) throw new Error("Unable to compress image");
        if (blob.type !== file.type || blob.size >= file.size) return file;

        return new File([blob], file.name, { type: file.type, lastModified: file.lastModified });
    } finally {
        image.close();
    }
};

const emptyForm = {
    category: "",
    profileParticulars: "",
    specimenRequired: "",
    actualPrice: "",
    offerPrice: "",
    machineType: "",
};

const emptyTrackingForm = {
    petId: "",
    petName: "",
    species: "",
    mobileNumber: "",
    labName: "",
    testName: "",
    date: "",
};

const emptyTrackingFilters = {
    fromDate: "",
    toDate: "",
    labName: "",
    testName: "",
    petId: "",
};

function BloodTestAdminModal({ show, onClose, clinicId }) {
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [newTest, setNewTest] = useState(emptyForm);
    const [newTestError, setNewTestError] = useState("");
    const [searchText, setSearchText] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchOpen, setSearchOpen] = useState(false);
    const searchBlurTimer = React.useRef(null);
    const [activeTab, setActiveTab] = useState("master");
    const [trackingRecords, setTrackingRecords] = useState([]);
    const [trackingLoading, setTrackingLoading] = useState(false);
    const [trackingSaving, setTrackingSaving] = useState(false);
    const [uploadingReportsId, setUploadingReportsId] = useState(null);
    const [downloadingReportId, setDownloadingReportId] = useState(null);
    const [trackingError, setTrackingError] = useState("");
    const [trackingMessage, setTrackingMessage] = useState("");
    const [trackingFormError, setTrackingFormError] = useState("");
    const [trackingForm, setTrackingForm] = useState(emptyTrackingForm);
    const [trackingFilters, setTrackingFilters] = useState(emptyTrackingFilters);
    const [editingTrackingId, setEditingTrackingId] = useState(null);
    const [pendingTrackingDeleteId, setPendingTrackingDeleteId] = useState(null);
    const [deletingTrackingId, setDeletingTrackingId] = useState(null);
    const [petSearchMobile, setPetSearchMobile] = useState("");
    const [petSearchResults, setPetSearchResults] = useState([]);
    const [petSearchLoading, setPetSearchLoading] = useState(false);
    const [petSearchMessage, setPetSearchMessage] = useState("");
    const [reportsTooltip, setReportsTooltip] = useState(null);
    const reportsTooltipRef = React.useRef(null);
    const reportsTooltipTriggerRef = React.useRef(null);

    const canUseDecimal = (value) => /^[0-9]*\.?[0-9]*$/.test(value);

    const fetchAllTests = async () => {
        try {
            setLoading(true);
            setError("");
            setMessage("");

            const token = localStorage.getItem("token");
            const res = await fetch(`${BASE_URL}/labTest`, {
                headers: token
                    ? {
                        Authorization: `Bearer ${token}`
                    }
                    : {}
            });

            if (!res.ok) {
                throw new Error("Failed to load lab tests");
            }

            const data = await res.json();
            setTests(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setError("Unable to load lab tests");
        } finally {
            setLoading(false);
        }
    };

    const trackingHeaders = () => {
        const token = localStorage.getItem("token");
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const loadTrackingRecords = async (filters = emptyTrackingFilters) => {
        try {
            setTrackingLoading(true);
            setTrackingError("");
            setTrackingMessage("");

            const query = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (String(value).trim()) query.set(key, String(value).trim());
            });
            const hasFilters = Array.from(query.keys()).length > 0;
            const endpoint = hasFilters
                ? `${BASE_URL}/lab-test-tracking/search?${query.toString()}`
                : `${BASE_URL}/lab-test-tracking`;
            const res = await fetch(endpoint, { headers: trackingHeaders() });

            if (!res.ok) {
                throw new Error("Failed to load lab test tracking records");
            }

            const data = await res.json();
            if (!Array.isArray(data)) {
                throw new Error("Unexpected lab test tracking response");
            }
            setTrackingRecords(data);
        } catch (err) {
            console.error(err);
            setTrackingError("Unable to load lab test tracking records");
        } finally {
            setTrackingLoading(false);
        }
    };

    const openTrackingTab = () => {
        setActiveTab("tracking");
        setTrackingFilters(emptyTrackingFilters);
        setTrackingForm(emptyTrackingForm);
        setEditingTrackingId(null);
        setTrackingFormError("");
        setPetSearchMobile("");
        setPetSearchResults([]);
        setPetSearchMessage("");
        loadTrackingRecords();
    };

    const searchTrackingRecords = () => {
        if (
            (trackingFilters.fromDate && trackingFilters.toDate && trackingFilters.fromDate > trackingFilters.toDate) ||
            Boolean(trackingFilters.fromDate) !== Boolean(trackingFilters.toDate)
        ) {
            setTrackingError("Enter both dates and ensure the start date is not after the end date");
            return;
        }
        loadTrackingRecords(trackingFilters);
    };

    const updateTrackingForm = (field, value) => {
        setTrackingForm((previous) => ({ ...previous, [field]: value }));
        setTrackingFormError("");
    };

    const searchPetsByMobile = async () => {
        const mobile = petSearchMobile.trim();
        if (!/^\d{10}$/.test(mobile)) {
            setPetSearchResults([]);
            setPetSearchMessage("Enter a valid 10-digit mobile number");
            return;
        }

        try {
            setPetSearchLoading(true);
            setPetSearchMessage("");
            setPetSearchResults([]);
            const res = await fetch(`${BASE_URL}/pets/search?mobile=${encodeURIComponent(mobile)}`, {
                headers: trackingHeaders(),
            });
            if (!res.ok) {
                throw new Error("Failed to search pets by mobile number");
            }

            const pets = await res.json();
            if (!Array.isArray(pets)) {
                throw new Error("Unexpected pet search response");
            }
            setPetSearchResults(pets);
            setPetSearchMessage(pets.length ? "" : "No pets found for this mobile number");
        } catch (err) {
            console.error(err);
            setPetSearchMessage("Unable to search pets. Please try again.");
        } finally {
            setPetSearchLoading(false);
        }
    };

    const selectTrackingPet = (petId) => {
        const pet = petSearchResults.find((item) => String(item.id) === petId);
        if (!pet) return;
        setTrackingForm((previous) => ({
            ...previous,
            petId: String(pet.id),
            petName: pet.name || "",
            species: pet.species || "",
            mobileNumber: String(pet.mobileNumber || pet.mobile || pet.ownerMobileNumber || petSearchMobile),
        }));
        setTrackingFormError("");
    };

    const editTrackingRecord = (record) => {
        setEditingTrackingId(record.id);
        setTrackingForm({
            petId: String(record.petId ?? ""),
            petName: record.petName || "",
            species: record.species || "",
            mobileNumber: String(record.mobileNumber || record.mobile || ""),
            labName: record.labName || "",
            testName: record.testName || "",
            date: record.date ? String(record.date).slice(0, 10) : "",
        });
        setTrackingFormError("");
        setTrackingMessage("");
        setPetSearchMobile("");
        setPetSearchResults([]);
        setPetSearchMessage("");
    };

    const cancelTrackingEdit = () => {
        setEditingTrackingId(null);
        setTrackingForm(emptyTrackingForm);
        setTrackingFormError("");
        setPetSearchMobile("");
        setPetSearchResults([]);
        setPetSearchMessage("");
    };

    const deleteTrackingRecord = async (id) => {
        try {
            setDeletingTrackingId(id);
            setTrackingError("");
            setTrackingMessage("");
            const res = await fetch(`${BASE_URL}/lab-test-tracking/${encodeURIComponent(id)}`, {
                method: "DELETE",
                headers: trackingHeaders(),
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Failed to delete lab test tracking record");
            }

            await loadTrackingRecords(trackingFilters);
            setTrackingMessage("Tracking record deleted successfully");
        } catch (err) {
            console.error(err);
            setTrackingError("Failed to delete lab test tracking record");
        } finally {
            setDeletingTrackingId(null);
        }
    };

    const confirmDeleteTrackingRecord = async () => {
        if (pendingTrackingDeleteId === null) return;
        const id = pendingTrackingDeleteId;
        setPendingTrackingDeleteId(null);
        await deleteTrackingRecord(id);
    };

    const saveTrackingRecord = async () => {
        const petId = Number(trackingForm.petId);
        if (!Number.isInteger(petId) || petId <= 0) {
            setTrackingFormError("Enter a valid pet ID");
            return;
        }
        if (!/^\d{10}$/.test(trackingForm.mobileNumber.trim())) {
            setTrackingFormError("Search by mobile number and select the pet to save a valid owner mobile number");
            return;
        }
        if (
            !trackingForm.petName.trim() ||
            !trackingForm.species.trim() ||
            !trackingForm.labName.trim() ||
            !trackingForm.testName.trim() ||
            !trackingForm.date
        ) {
            setTrackingFormError("Complete all tracking fields");
            return;
        }

        try {
            setTrackingSaving(true);
            setTrackingError("");
            setTrackingMessage("");

            const payload = {
                petId,
                petName: trackingForm.petName.trim(),
                species: trackingForm.species.trim(),
                mobileNumber: trackingForm.mobileNumber.trim(),
                labName: trackingForm.labName.trim(),
                testName: trackingForm.testName.trim(),
                date: trackingForm.date,
            };
            const isEditing = editingTrackingId !== null;
            const res = await fetch(
                `${BASE_URL}/lab-test-tracking${isEditing ? `/${encodeURIComponent(editingTrackingId)}` : ""}`,
                {
                    method: isEditing ? "PUT" : "POST",
                    headers: {
                        ...trackingHeaders(),
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Failed to save lab test tracking record");
            }

            setTrackingForm(emptyTrackingForm);
            setEditingTrackingId(null);
            setPetSearchMobile("");
            setPetSearchResults([]);
            setPetSearchMessage("");
            await loadTrackingRecords(trackingFilters);
            setTrackingMessage(isEditing ? "Tracking record updated successfully" : "Tracking record added successfully");
        } catch (err) {
            console.error(err);
            setTrackingError("Failed to save lab test tracking record");
        } finally {
            setTrackingSaving(false);
        }
    };

    const uploadTrackingReports = async (recordId, files) => {
        if (!files.length) return;

        try {
            setUploadingReportsId(recordId);
            setTrackingError("");
            setTrackingMessage("");

            const compressionFailures = [];
            const compressedFiles = [];
            for (const file of files) {
                try {
                    compressedFiles.push(await compressImageFile(file));
                } catch (compressionError) {
                    console.warn(`Unable to compress ${file.name}; uploading the original file instead.`, compressionError);
                    compressionFailures.push(file);
                    compressedFiles.push(file);
                }
            }

            const formData = new FormData();
            compressedFiles.forEach((file) => formData.append("files", file));
            const res = await fetch(
                `${BASE_URL}/lab-test-tracking/${encodeURIComponent(recordId)}/reports`,
                {
                    method: "POST",
                    headers: trackingHeaders(),
                    body: formData,
                }
            );

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Failed to upload reports");
            }

            await loadTrackingRecords(trackingFilters);
            const compressedCount = compressedFiles.filter((file, index) => file !== files[index]).length;
            const compressedPdfCount = compressedFiles.filter(
                (file, index) => files[index].type === "application/pdf" && file !== files[index]
            ).length;
            const failedPdfCount = compressionFailures.filter((file) => file.type === "application/pdf").length;
            const failedOtherCount = compressionFailures.length - failedPdfCount;
            const pdfIndexes = files
                .map((file, index) => file.type === "application/pdf" ? index : -1)
                .filter((index) => index !== -1);
            const compressionWarning = failedOtherCount > 0
                ? ` Compression failed for ${failedOtherCount} other file(s); the original file(s) were uploaded.`
                : "";
            if (pdfIndexes.length > 0) {
                const originalPdfBytes = pdfIndexes.reduce((total, index) => total + files[index].size, 0);
                const compressedPdfBytes = pdfIndexes.reduce((total, index) => total + compressedFiles[index].size, 0);
                const pdfMessage = compressedPdfCount > 0
                    ? `Compressed ${compressedPdfCount} of ${pdfIndexes.length} PDF(s): ${formatFileSize(originalPdfBytes)} to ${formatFileSize(compressedPdfBytes)}.`
                    : failedPdfCount > 0
                        ? `PDF compression failed for ${failedPdfCount} PDF(s); original PDF(s) uploaded.`
                        : `PDF compression did not reduce size (${formatFileSize(originalPdfBytes)}); original PDF(s) uploaded.`;
                const otherCompressedCount = compressedCount - compressedPdfCount;
                setTrackingMessage(
                    `Reports uploaded successfully. ${pdfMessage}${otherCompressedCount > 0 ? ` Also compressed ${otherCompressedCount} other file(s).` : ""}${compressionWarning}`
                );
            } else {
                setTrackingMessage(
                    compressedCount > 0
                        ? `Reports uploaded successfully. Compressed ${compressedCount} image(s).${compressionWarning}`
                        : `Reports uploaded successfully${compressionWarning}`
                );
            }
        } catch (err) {
            console.error(err);
            setTrackingError(err.message || "Failed to upload reports");
        } finally {
            setUploadingReportsId(null);
        }
    };

    const toggleReportsTooltip = (event, record) => {
        if (reportsTooltip?.recordId === record.id) {
            setReportsTooltip(null);
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const width = Math.min(320, window.innerWidth - 16);
        setReportsTooltip({
            recordId: record.id,
            left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
            top: Math.max(8, Math.min(rect.bottom + 8, window.innerHeight - 368)),
        });
    };

    useEffect(() => {
        if (!reportsTooltip) return undefined;

        const closeOnOutsideClick = (event) => {
            if (
                !reportsTooltipRef.current?.contains(event.target) &&
                !reportsTooltipTriggerRef.current?.contains(event.target)
            ) {
                setReportsTooltip(null);
            }
        };
        const closeOnEscape = (event) => {
            if (event.key === "Escape") setReportsTooltip(null);
        };
        const closeOnViewportChange = () => setReportsTooltip(null);

        document.addEventListener("pointerdown", closeOnOutsideClick);
        document.addEventListener("keydown", closeOnEscape);
        window.addEventListener("resize", closeOnViewportChange);
        window.addEventListener("scroll", closeOnViewportChange, true);
        return () => {
            document.removeEventListener("pointerdown", closeOnOutsideClick);
            document.removeEventListener("keydown", closeOnEscape);
            window.removeEventListener("resize", closeOnViewportChange);
            window.removeEventListener("scroll", closeOnViewportChange, true);
        };
    }, [reportsTooltip]);

    useEffect(() => {
        if (!show || activeTab !== "tracking") setReportsTooltip(null);
    }, [show, activeTab]);

    const downloadTrackingReport = async (recordId, report) => {
        const reportId = report.id ?? report.reportId;
        if (reportId == null) return;
        const downloadKey = `${recordId}-${reportId}`;

        try {
            setDownloadingReportId(downloadKey);
            setTrackingError("");
            const res = await fetch(
                `${BASE_URL}/lab-test-tracking/${encodeURIComponent(recordId)}/reports/${encodeURIComponent(reportId)}/download`,
                { headers: trackingHeaders() }
            );
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Failed to download report");
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = report.fileName || report.originalFileName || report.name || `report-${reportId}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            setTrackingError("Failed to download report");
        } finally {
            setDownloadingReportId(null);
        }
    };

    useEffect(() => {
        if (!show) return;

        setActiveTab("master");
        setNewTest({
            ...emptyForm
        });
        setNewTestError("");
        setSearchText("");
        setCategoryFilter("");
        setPageSize(10);
        setCurrentPage(1);
        setSearchOpen(false);
        fetchAllTests();
    }, [show, clinicId]);

    useEffect(() => {
        if (!show) {
            setPendingDeleteId(null);
        }
    }, [show]);

    const sortedTests = useMemo(
        () => [...tests].sort((a, b) => (a.id || 0) - (b.id || 0)),
        [tests]
    );

    const filteredTests = useMemo(() => {
        const query = searchText.trim().toLowerCase();

        return sortedTests.filter((row) => {
            const matchesCategory = !categoryFilter || (row.category || "").toLowerCase() === categoryFilter.toLowerCase();

            const matchesQuery =
                !query ||
                String(row.id || "").includes(query) ||
                (row.category || "").toLowerCase().includes(query) ||
                (row.profileParticulars || "").toLowerCase().includes(query) ||
                (row.specimenRequired || "").toLowerCase().includes(query) ||
                (row.machineType || "").toLowerCase().includes(query);

            return matchesCategory && matchesQuery;
        });
    }, [sortedTests, searchText, categoryFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredTests.length / pageSize));

    const paginatedTests = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredTests.slice(start, start + pageSize);
    }, [filteredTests, currentPage, pageSize]);

    const searchSuggestions = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (query.length < 2) return [];

        return filteredTests.slice(0, 8);
    }, [searchText, filteredTests]);

    const handleSearchFocus = () => {
        if (searchText.trim().length >= 2) {
            setSearchOpen(true);
        }
    };

    const handleSearchBlur = () => {
        window.clearTimeout(searchBlurTimer.current);
        searchBlurTimer.current = window.setTimeout(() => {
            setSearchOpen(false);
        }, 150);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, categoryFilter, pageSize]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const startEdit = (row) => {
        setEditingId(row.id);
        setEditDraft({
            id: row.id,
            category: row.category || "",
            profileParticulars: row.profileParticulars || "",
            specimenRequired: row.specimenRequired || "",
            actualPrice: row.actualPrice ?? "",
            offerPrice: row.offerPrice ?? "",
            machineType: row.machineType || "",
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditDraft(null);
    };

    const updateDraft = (field, value) => {
        if ((field === "actualPrice" || field === "offerPrice") && !canUseDecimal(value)) {
            return;
        }

        setEditDraft((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const saveRow = async () => {
        if (!editDraft) return;

        try {
            setSavingId(editDraft.id);
            setError("");
            setMessage("");

            const token = localStorage.getItem("token");
            const payload = {
                category: editDraft.category || "",
                profileParticulars: editDraft.profileParticulars || "",
                specimenRequired: editDraft.specimenRequired || "",
                actualPrice: Number(editDraft.actualPrice || 0),
                offerPrice: Number(editDraft.offerPrice || 0),
                machineType: editDraft.machineType || "",
                clinicId: clinicId || null
            };

            const res = await fetch(`${BASE_URL}/labTest/${editDraft.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Failed to update lab test");
            }

            setMessage("Lab test updated successfully");
            await fetchAllTests();
            cancelEdit();
        } catch (err) {
            console.error(err);
            setError("Failed to update lab test");
        } finally {
            setSavingId(null);
        }
    };

    const deleteRow = async (id) => {
        try {
            setDeletingId(id);
            setError("");
            setMessage("");

            const token = localStorage.getItem("token");
            const res = await fetch(`${BASE_URL}/labTest/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!res.ok) {
                throw new Error("Delete failed");
            }

            setMessage("Lab test deleted successfully");
            setTests((prev) => prev.filter((item) => item.id !== id));
        } catch (err) {
            console.error(err);
            setError("Failed to delete lab test");
        } finally {
            setDeletingId(null);
        }
    };

    const requestDeleteRow = (id) => {
        setPendingDeleteId(id);
        setError("");
        setMessage("");
    };

    const cancelDeleteRow = () => {
        setPendingDeleteId(null);
    };

    const confirmDeleteRow = async () => {
        if (!pendingDeleteId) return;
        const id = pendingDeleteId;
        setPendingDeleteId(null);
        await deleteRow(id);
    };

    const handleNewTestChange = (e) => {
        const { name, value } = e.target;

        if (name === "actualPrice" || name === "offerPrice") {
            if (!canUseDecimal(value)) return;
        }

        setNewTest((prev) => ({
            ...prev,
            [name]: value
        }));

        setNewTestError("");
    };

    const clearFilters = () => {
        setSearchText("");
        setCategoryFilter("");
        setPageSize(10);
        setCurrentPage(1);
        setSearchOpen(false);
    };

    const validateNewTest = () => {
        if (!newTest.category.trim()) return "Category is required";
        if (!newTest.profileParticulars.trim()) return "Profile particulars are required";
        if (!newTest.specimenRequired.trim()) return "Specimen required is required";
        if (!newTest.machineType.trim()) return "Machine type is required";
        if (!newTest.actualPrice.trim()) return "Actual price is required";
        if (!newTest.offerPrice.trim()) return "Offer price is required";
        return "";
    };

    const addNewTest = async () => {
        const validationError = validateNewTest();
        if (validationError) {
            setNewTestError(validationError);
            return;
        }

        try {
            setLoading(true);
            setError("");
            setMessage("");

            const token = localStorage.getItem("token");
            const payload = {
                category: newTest.category.trim(),
                profileParticulars: newTest.profileParticulars.trim(),
                specimenRequired: newTest.specimenRequired.trim(),
                actualPrice: Number(newTest.actualPrice),
                offerPrice: Number(newTest.offerPrice),
                machineType: newTest.machineType.trim(),
                clinicId: clinicId || null
            };

            const res = await fetch(`${BASE_URL}/labTest`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Failed to add lab test");
            }

            setMessage("Lab test added successfully");
            setNewTest({
                ...emptyForm
            });
            setSearchText("");
            setCategoryFilter("");
            setCurrentPage(1);
            setSearchOpen(false);
            await fetchAllTests();
        } catch (err) {
            console.error(err);
            setError("Failed to add lab test");
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <>
            <div className="modal-backdrop fade show"></div>

            <div className="modal show fade d-block" tabIndex="-1">
                <div className="modal-dialog modal-xl">
                    <div className="modal-content position-relative">
                        {pendingDeleteId && (
                            <div
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 30,
                                    background: "rgba(15, 23, 42, 0.35)",
                                    backdropFilter: "blur(1px)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "1rem"
                                }}
                            >
                                <div
                                    className="bg-white rounded-3 shadow-lg p-4"
                                    style={{ width: "min(420px, 100%)" }}
                                >
                                    <div className="fw-semibold mb-2">Delete this lab test?</div>
                                    <div className="text-muted small mb-3">
                                        This action cannot be undone.
                                    </div>
                                    <div className="d-flex justify-content-end gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={cancelDeleteRow}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={confirmDeleteRow}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {pendingTrackingDeleteId !== null && (
                            <div
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 30,
                                    background: "rgba(15, 23, 42, 0.35)",
                                    backdropFilter: "blur(1px)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "1rem"
                                }}
                            >
                                <div
                                    className="bg-white rounded-3 shadow-lg p-4"
                                    style={{ width: "min(420px, 100%)" }}
                                >
                                    <div className="fw-semibold mb-2">Delete this tracking record?</div>
                                    <div className="text-muted small mb-3">
                                        This action cannot be undone.
                                    </div>
                                    <div className="d-flex justify-content-end gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => setPendingTrackingDeleteId(null)}
                                            disabled={deletingTrackingId !== null}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={confirmDeleteTrackingRecord}
                                            disabled={deletingTrackingId !== null}
                                        >
                                            {deletingTrackingId !== null ? "Deleting..." : "Delete"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="modal-header">
                            <h5 className="modal-title">Lab Test Master</h5>
                            <button className="btn-close" onClick={onClose}></button>
                        </div>

                        <div className="modal-body" style={{ maxHeight: "76vh", overflowY: "auto" }}>
                            <ul className="nav nav-tabs mb-3" role="tablist">
                                <li className="nav-item" role="presentation">
                                    <button
                                        type="button"
                                        className={`nav-link${activeTab === "master" ? " active" : ""}`}
                                        onClick={() => setActiveTab("master")}
                                    >
                                        Lab Test Master
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button
                                        type="button"
                                        className={`nav-link${activeTab === "tracking" ? " active" : ""}`}
                                        onClick={() => {
                                            if (activeTab !== "tracking") openTrackingTab();
                                        }}
                                    >
                                        Lab Test Tracking
                                    </button>
                                </li>
                            </ul>

                            {activeTab === "master" && (
                                <>
                                    {loading && (
                                        <div className="modal-loading-overlay">
                                            <div className="text-center">
                                                <div className="spinner-border" role="status"></div>
                                                <p className="mt-2 mb-0">Loading...</p>
                                            </div>
                                        </div>
                                    )}

                                    {message && <div className="alert alert-success py-2">{message}</div>}
                                    {error && <div className="alert alert-danger py-2">{error}</div>}

                                    <div className="card mb-3 p-3">
                                        <h6 className="mb-3">Add New Lab Test</h6>

                                        {newTestError && (
                                            <div className="alert alert-danger py-2">{newTestError}</div>
                                        )}

                                        <div className="row g-2">
                                            <div className="col-md-3">
                                                <input
                                                    className="form-control"
                                                    name="category"
                                                    placeholder="Category"
                                                    value={newTest.category}
                                                    onChange={handleNewTestChange}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <input
                                                    className="form-control"
                                                    name="profileParticulars"
                                                    placeholder="Profile Particulars"
                                                    value={newTest.profileParticulars}
                                                    onChange={handleNewTestChange}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <input
                                                    className="form-control"
                                                    name="specimenRequired"
                                                    placeholder="Specimen Required"
                                                    value={newTest.specimenRequired}
                                                    onChange={handleNewTestChange}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <input
                                                    className="form-control"
                                                    name="machineType"
                                                    placeholder="Machine Type"
                                                    value={newTest.machineType}
                                                    onChange={handleNewTestChange}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="offerPrice"
                                                    placeholder="Offer Price"
                                                    value={newTest.offerPrice}
                                                    onChange={handleNewTestChange}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="actualPrice"
                                                    placeholder="Actual Price"
                                                    value={newTest.actualPrice}
                                                    onChange={handleNewTestChange}
                                                />
                                            </div>
                                            <div className="col-md-3 d-flex align-items-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-success w-100"
                                                    onClick={addNewTest}
                                                >
                                                    Add Test
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card mb-3 p-3">
                                <div className="row g-2 align-items-end">
                                    <div className="col-md-5">
                                        <label className="form-label mb-1">Search tests</label>
                                        <div style={{ position: "relative" }}>
                                            <input
                                                className="form-control"
                                                placeholder="Search by name, category, machine, specimen, or ID"
                                                value={searchText}
                                                onChange={(e) => {
                                                    setSearchText(e.target.value);
                                                    setSearchOpen(e.target.value.trim().length >= 2);
                                                }}
                                                onFocus={handleSearchFocus}
                                                onBlur={handleSearchBlur}
                                                autoComplete="off"
                                            />

                                            {searchOpen && searchSuggestions.length > 0 && (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: "100%",
                                                        left: 0,
                                                        right: 0,
                                                        zIndex: 20,
                                                        background: "#fff",
                                                        border: "1px solid #d6deeb",
                                                        borderRadius: "0 0 14px 14px",
                                                        maxHeight: "260px",
                                                        overflowY: "auto",
                                                        boxShadow: "0 12px 24px rgba(15,23,42,0.12)"
                                                    }}
                                                >
                                                    {searchSuggestions.map((item) => (
                                                        <button
                                                            key={item.id}
                                                            type="button"
                                                            className="w-100 text-start border-0 bg-white"
                                                            style={{
                                                                padding: "10px 12px",
                                                                borderBottom: "1px solid #eef2f7"
                                                            }}
                                                            onClick={() => {
                                                                setSearchText(item.profileParticulars || String(item.id || ""));
                                                                setSearchOpen(false);
                                                            }}
                                                        >
                                                            <div className="fw-semibold">{item.profileParticulars || "-"}</div>
                                                            <div className="small text-muted">
                                                                ID: {item.id} | Category: {item.category || "-"} | Price: ₹ {item.actualPrice ?? 0}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label mb-1">Category filter</label>
                                        <select
                                            className="form-select"
                                            value={categoryFilter}
                                            onChange={(e) => setCategoryFilter(e.target.value)}
                                        >
                                            <option value="">All Categories</option>
                                            {Array.from(
                                                new Set(sortedTests.map((item) => item.category).filter(Boolean))
                                            ).map((category) => (
                                                <option key={category} value={category}>
                                                    {category}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-md-2">
                                        <label className="form-label mb-1">Rows</label>
                                        <select
                                            className="form-select"
                                            value={pageSize}
                                            onChange={(e) => setPageSize(Number(e.target.value))}
                                        >
                                            <option value="5">5</option>
                                            <option value="10">10</option>
                                            <option value="20">20</option>
                                            <option value="50">50</option>
                                        </select>
                                    </div>

                                    <div className="col-md-2">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary w-100"
                                            onClick={() => {
                                                clearFilters();
                                                setSearchOpen(false);
                                            }}
                                        >
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-bordered table-sm align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>ID</th>
                                            <th>Category</th>
                                            <th>Profile Particulars</th>
                                            <th>Specimen Required</th>
                                            <th>Machine Type</th>
                                            <th>Offer Price</th>
                                            <th>Actual Price</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedTests.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="text-center text-muted">
                                                    No lab tests found
                                                </td>
                                            </tr>
                                        ) : (
                                            paginatedTests.map((row) => (
                                                <tr key={row.id}>
                                                    <td>{row.id}</td>
                                                    <td>
                                                        {editingId === row.id ? (
                                                            <input
                                                                className="form-control form-control-sm"
                                                                value={editDraft?.category || ""}
                                                                onChange={(e) => updateDraft("category", e.target.value)}
                                                            />
                                                        ) : (
                                                            row.category || "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {editingId === row.id ? (
                                                            <input
                                                                className="form-control form-control-sm"
                                                                value={editDraft?.profileParticulars || ""}
                                                                onChange={(e) => updateDraft("profileParticulars", e.target.value)}
                                                            />
                                                        ) : (
                                                            row.profileParticulars || "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {editingId === row.id ? (
                                                            <input
                                                                className="form-control form-control-sm"
                                                                value={editDraft?.specimenRequired || ""}
                                                                onChange={(e) => updateDraft("specimenRequired", e.target.value)}
                                                            />
                                                        ) : (
                                                            row.specimenRequired || "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {editingId === row.id ? (
                                                            <input
                                                                className="form-control form-control-sm"
                                                                value={editDraft?.machineType || ""}
                                                                onChange={(e) => updateDraft("machineType", e.target.value)}
                                                            />
                                                        ) : (
                                                            row.machineType || "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {editingId === row.id ? (
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                value={editDraft?.offerPrice ?? ""}
                                                                onChange={(e) => updateDraft("offerPrice", e.target.value)}
                                                            />
                                                        ) : (
                                                            row.offerPrice ?? "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {editingId === row.id ? (
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                value={editDraft?.actualPrice ?? ""}
                                                                onChange={(e) => updateDraft("actualPrice", e.target.value)}
                                                            />
                                                        ) : (
                                                            row.actualPrice ?? "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="d-inline-flex align-items-center gap-2 flex-nowrap" style={{ whiteSpace: "nowrap" }}>
                                                            {editingId === row.id ? (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-success"
                                                                        onClick={saveRow}
                                                                        disabled={savingId === row.id || deletingId === row.id}
                                                                    >
                                                                        {savingId === row.id ? "Saving..." : "Save"}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-secondary"
                                                                        onClick={cancelEdit}
                                                                        disabled={savingId === row.id || deletingId === row.id}
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    onClick={() => startEdit(row)}
                                                                    disabled={savingId === row.id || deletingId === row.id}
                                                                    title="Update lab test"
                                                                    aria-label={`Update lab test ${row.id}`}
                                                                >
                                                                    <Pencil size={16} aria-hidden="true" />
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => requestDeleteRow(row.id)}
                                                                disabled={savingId === row.id || deletingId === row.id || editingId === row.id}
                                                                title="Delete lab test"
                                                                aria-label={`Delete lab test ${row.id}`}
                                                            >
                                                                <Trash2 size={16} aria-hidden="true" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                                <div className="text-muted small">
                                    {filteredTests.length === 0
                                        ? "No rows to display"
                                        : `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, filteredTests.length)} of ${filteredTests.length}`}
                                </div>

                                <div className="d-flex gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                        disabled={currentPage <= 1}
                                    >
                                        Prev
                                    </button>

                                    <span className="align-self-center small fw-semibold">
                                        Page {currentPage} of {totalPages}
                                    </span>

                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage >= totalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                            </>
                        )}

                        {activeTab === "tracking" && (
                            <>
                                {trackingMessage && <div className="alert alert-success py-2">{trackingMessage}</div>}
                                {trackingError && <div className="alert alert-danger py-2">{trackingError}</div>}

                                <div className="card mb-3 p-3">
                                    <h6 className="mb-3">
                                        {editingTrackingId === null ? "Add Lab Test Tracking" : `Update Tracking #${editingTrackingId}`}
                                    </h6>
                                    {trackingFormError && (
                                        <div className="alert alert-danger py-2">{trackingFormError}</div>
                                    )}
                                    <div className="row g-2">
                                        <div className="col-md-4">
                                            <label className="form-label mb-1">Owner Mobile Number</label>
                                            <div className="input-group">
                                                <input
                                                    type="tel"
                                                    inputMode="numeric"
                                                    maxLength="10"
                                                    className="form-control"
                                                    placeholder="Enter 10-digit mobile number"
                                                    value={petSearchMobile}
                                                    onChange={(e) => {
                                                        setPetSearchMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
                                                        setPetSearchResults([]);
                                                        setPetSearchMessage("");
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") searchPetsByMobile();
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary"
                                                    onClick={searchPetsByMobile}
                                                    disabled={petSearchLoading}
                                                >
                                                    {petSearchLoading ? "Searching..." : "Find Pets"}
                                                </button>
                                            </div>
                                            {petSearchMessage && (
                                                <div className={`small mt-1 ${petSearchResults.length ? "text-success" : "text-danger"}`}>
                                                    {petSearchMessage}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label mb-1">Select Pet</label>
                                            <select
                                                className="form-select"
                                                value={trackingForm.petId}
                                                onChange={(e) => selectTrackingPet(e.target.value)}
                                                disabled={!petSearchResults.length}
                                            >
                                                <option value="">
                                                    {petSearchResults.length ? "Choose a pet" : "Search by mobile first"}
                                                </option>
                                                {trackingForm.petId && !petSearchResults.some((pet) => String(pet.id) === trackingForm.petId) && (
                                                    <option value={trackingForm.petId}>
                                                        {trackingForm.petName || "Selected pet"} ({trackingForm.species || "Unknown species"}) - ID {trackingForm.petId}
                                                    </option>
                                                )}
                                                {petSearchResults.map((pet) => (
                                                    <option key={pet.id} value={String(pet.id)}>
                                                        {pet.name || "Unnamed pet"} ({pet.species || "Unknown species"}) - ID {pet.id}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label mb-1">Selected Pet</label>
                                            <div className="form-control bg-light" aria-live="polite">
                                                {trackingForm.petId
                                                    ? `${trackingForm.petName || "Unnamed pet"} (${trackingForm.species || "Unknown species"}) - ID ${trackingForm.petId}`
                                                    : "No pet selected"}
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label mb-1">Owner Mobile Saved</label>
                                            <input
                                                className="form-control bg-light"
                                                value={trackingForm.mobileNumber}
                                                placeholder="Select a pet to populate"
                                                readOnly
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label mb-1">Lab Name</label>
                                            <select
                                                className="form-select"
                                                value={trackingForm.labName}
                                                onChange={(e) => updateTrackingForm("labName", e.target.value)}
                                            >
                                                <option value="">Select lab</option>
                                                <option value="All Well Animal">All Well Animal</option>
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label mb-1">Test Name</label>
                                            <input
                                                className="form-control"
                                                value={trackingForm.testName}
                                                onChange={(e) => updateTrackingForm("testName", e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label mb-1">Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={trackingForm.date}
                                                onChange={(e) => updateTrackingForm("date", e.target.value)}
                                            />
                                        </div>
                                        <div className="col-12 d-flex gap-2 justify-content-end">
                                            {editingTrackingId !== null && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={cancelTrackingEdit}
                                                    disabled={trackingSaving}
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-success"
                                                onClick={saveTrackingRecord}
                                                disabled={trackingSaving}
                                            >
                                                {trackingSaving ? "Saving..." : editingTrackingId === null ? "Add Tracking" : "Update Tracking"}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="card mb-3 p-3">
                                    <h6 className="mb-3">Search Tracking Records</h6>
                                    <div className="row g-2 align-items-end">
                                        <div className="col-md-2">
                                            <label className="form-label mb-1">From Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={trackingFilters.fromDate}
                                                onChange={(e) => setTrackingFilters((previous) => ({ ...previous, fromDate: e.target.value }))}
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label mb-1">To Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={trackingFilters.toDate}
                                                onChange={(e) => setTrackingFilters((previous) => ({ ...previous, toDate: e.target.value }))}
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label mb-1">Lab Name</label>
                                            <select
                                                className="form-select"
                                                value={trackingFilters.labName}
                                                onChange={(e) => setTrackingFilters((previous) => ({ ...previous, labName: e.target.value }))}
                                            >
                                                <option value="">All Labs</option>
                                                <option value="All Well Animal">All Well Animal</option>
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label mb-1">Test Name</label>
                                            <input
                                                className="form-control"
                                                value={trackingFilters.testName}
                                                onChange={(e) => setTrackingFilters((previous) => ({ ...previous, testName: e.target.value }))}
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label mb-1">Pet ID</label>
                                            <input
                                                type="number"
                                                min="1"
                                                className="form-control"
                                                value={trackingFilters.petId}
                                                onChange={(e) => setTrackingFilters((previous) => ({ ...previous, petId: e.target.value }))}
                                            />
                                        </div>
                                        <div className="col-md-2 d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-primary flex-grow-1"
                                                onClick={searchTrackingRecords}
                                                disabled={trackingLoading}
                                            >
                                                Search
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => {
                                                    setTrackingFilters(emptyTrackingFilters);
                                                    loadTrackingRecords();
                                                }}
                                                disabled={trackingLoading}
                                            >
                                                Clear
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {trackingLoading && (
                                    <div className="text-center text-muted py-3">
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Loading tracking records...
                                    </div>
                                )}
                                <div className="small text-muted mb-2">
                                    PDF compression rasterizes pages; searchable text, links, and form fields are not preserved.
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-bordered table-sm align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th>ID</th>
                                                <th>Pet ID</th>
                                                <th>Pet Name</th>
                                                <th>Species</th>
                                                <th>Owner Mobile</th>
                                                <th>Lab Name</th>
                                                <th>Test Name</th>
                                                <th>Date</th>
                                                <th>Reports</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!trackingLoading && trackingRecords.length === 0 ? (
                                                <tr>
                                                    <td colSpan="10" className="text-center text-muted">
                                                        No tracking records found
                                                    </td>
                                                </tr>
                                            ) : (
                                                trackingRecords.map((record) => (
                                                    <tr key={record.id}>
                                                        <td>{record.id ?? "-"}</td>
                                                        <td>{record.petId ?? "-"}</td>
                                                        <td>{record.petName || "-"}</td>
                                                        <td>{record.species || "-"}</td>
                                                        <td>{record.mobileNumber || record.mobile || "-"}</td>
                                                        <td>{record.labName || "-"}</td>
                                                        <td>{record.testName || "-"}</td>
                                                        <td>{record.date ? String(record.date).slice(0, 10) : "-"}</td>
                                                        <td>
                                                            {Array.isArray(record.reports) && record.reports.length > 0 ? (
                                                                <button
                                                                    ref={reportsTooltip?.recordId === record.id ? reportsTooltipTriggerRef : null}
                                                                    type="button"
                                                                    className="btn btn-link btn-sm text-start p-0"
                                                                    aria-expanded={reportsTooltip?.recordId === record.id}
                                                                    aria-label={`Show ${record.reports.length} uploaded report${record.reports.length === 1 ? "" : "s"}`}
                                                                    onClick={(event) => toggleReportsTooltip(event, record)}
                                                                >
                                                                    {record.reports.length} report{record.reports.length === 1 ? "" : "s"}
                                                                </button>
                                                            ) : (
                                                                <span className="text-muted small">No reports</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-column align-items-start gap-1">
                                                                <div className="d-inline-flex align-items-center gap-1">
                                                                    <input
                                                                        id={`report-upload-${record.id}`}
                                                                        type="file"
                                                                        className="visually-hidden"
                                                                        multiple
                                                                        aria-label={`Upload reports for tracking record ${record.id}`}
                                                                        onChange={(event) => {
                                                                            const files = Array.from(event.currentTarget.files || []);
                                                                            event.currentTarget.value = "";
                                                                            uploadTrackingReports(record.id, files);
                                                                        }}
                                                                        disabled={record.id == null || uploadingReportsId === record.id}
                                                                    />
                                                                    <label
                                                                        htmlFor={`report-upload-${record.id}`}
                                                                        className={`btn btn-sm btn-outline-secondary mb-0${record.id == null || uploadingReportsId === record.id ? " disabled" : ""}`}
                                                                        title={uploadingReportsId === record.id ? "Uploading reports..." : "Upload reports"}
                                                                        aria-label={`Upload reports for tracking record ${record.id}`}
                                                                        aria-disabled={record.id == null || uploadingReportsId === record.id}
                                                                    >
                                                                        <Upload size={16} aria-hidden="true" />
                                                                    </label>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => editTrackingRecord(record)}
                                                                        disabled={record.id == null || trackingSaving || deletingTrackingId !== null || uploadingReportsId === record.id}
                                                                        title="Update tracking record"
                                                                        aria-label={`Update tracking record ${record.id}`}
                                                                    >
                                                                        <Pencil size={16} aria-hidden="true" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        onClick={() => setPendingTrackingDeleteId(record.id)}
                                                                        disabled={record.id == null || deletingTrackingId !== null || uploadingReportsId === record.id}
                                                                        title="Delete tracking record"
                                                                        aria-label={`Delete tracking record ${record.id}`}
                                                                    >
                                                                        <Trash2 size={16} aria-hidden="true" />
                                                                    </button>
                                                                </div>
                                                                {uploadingReportsId === record.id && (
                                                                    <span className="text-muted small" role="status">
                                                                        Compressing and uploading reports...
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                        </div>

                        {reportsTooltip && createPortal(
                            <div
                                ref={reportsTooltipRef}
                                role="dialog"
                                aria-label="Uploaded reports"
                                className="bg-white border rounded shadow p-3"
                                style={{
                                    position: "fixed",
                                    zIndex: 1080,
                                    left: reportsTooltip.left,
                                    top: reportsTooltip.top,
                                    width: "min(320px, calc(100vw - 16px))",
                                    maxHeight: "min(360px, calc(100vh - 16px))",
                                    overflowY: "auto",
                                }}
                            >
                                <div className="fw-semibold small mb-2">Uploaded reports</div>
                                <ul className="list-unstyled mb-0">
                                    {(trackingRecords.find((record) => record.id === reportsTooltip.recordId)?.reports || [])
                                        .map((report, index) => {
                                            const reportId = report.id ?? report.reportId;
                                            const downloadKey = `${reportsTooltip.recordId}-${reportId}`;
                                            const reportName = report.fileName || report.originalFileName || report.name || `Report ${reportId ?? index + 1}`;
                                            return (
                                                <li key={reportId ?? `${reportName}-${index}`}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm text-start p-0 text-break"
                                                        onClick={() => downloadTrackingReport(reportsTooltip.recordId, report)}
                                                        disabled={reportId == null || downloadingReportId === downloadKey}
                                                    >
                                                        {downloadingReportId === downloadKey ? "Downloading..." : reportName}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                </ul>
                            </div>,
                            document.body
                        )}

                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default BloodTestAdminModal;
